import { useCallback, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useStore } from "./store";
import { profilesById, devProfile } from "./seed/profiles";
import { useAuthStore, buildProfileFromUser } from "./authStore";
import { workspaces, workspacesById } from "./seed/workspaces";
import { fetchWithOfflineCache } from "./offlineDb";
import {
  extraProgrammes,
  karamojaProjects,
  type ProgrammeSummary,
  type ProjectSummary,
} from "./seed/stratus";
import { formatMoneyIn, formatCompactCurrency, convert, type CurrencyCode } from "@/design/format";
import type {
  WorkspaceSeed,
  AuditEvent,
  Profile,
  Workspace,
  Project,
  SubProject,
  Milestone,
  Person,
  Organisation,
  SpendRequest,
  Currency,
  Report,
  EvidenceItem,
  AuthorityGrant,
  AssuranceLevel,
  SpendItem,
  Connection,
  FeedTransaction,
  MaterialityConfig,
  AIFinding,
  AIReview,
  SiteVisit,
  SiteVisitQuestion,
} from "./types";
import { COMPULSORY_SITE_VISIT_QUESTIONS } from "./types";
import { ANALYST_NAME, reviewForAsk, findingsForAsk, verificationSummary, type VerificationSummary } from "./heimdall";
import { api, demoModeActive, type InvitableProject, type ProjectOrganisationSummary } from "./api";
import { deriveActionPanelItems, rankActionItems, ACTION_PANEL_TITLE, type ActionItem } from "./actionPanel";

// ---------------------------------------------------------------------------
// Data mappers — convert snake_case API rows to camelCase frontend types
// ---------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapApiProject(p: any, programmeId: string | number): Project {
  return {
    id: String(p.id),
    programmeId: String(programmeId),
    name: p.title ?? p.name ?? "",
    region: p.region ?? "",
    district: p.district ?? "",
    budgeted: Number(p.budgeted ?? p.targetamount ?? 0),
    spent: Number(p.spent ?? 0),
    evidenced: Number(p.evidenced ?? 0),
    redundancy: Number(p.redundancy ?? 0),
    // Prefer the org slug — the people/org layer (store) is keyed by slug ids.
    leadContractorId: String(p.leadcontractorslug ?? p.leadcontractorid ?? ""),
    // Raw numeric id, kept separately for identity comparisons against
    // profile.organisationId (also numeric) — see Project.leadContractorOrgId.
    leadContractorOrgId: p.leadcontractorid != null ? String(p.leadcontractorid) : undefined,
    status: (p.status ?? "active") as Project["status"],
    lat: p.lat != null ? Number(p.lat) : undefined,
    lng: p.lng != null ? Number(p.lng) : undefined,
    currentPhase: p.currentphase ?? undefined,
    scheduleDriftDays: p.scheduledriftdays != null ? Number(p.scheduledriftdays) : undefined,
    coverImageUrl: p.coverimageurl ?? undefined,
    description: p.description ?? undefined,
    procurementLineRef: p.procurementlineref ?? undefined,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapApiSubProject(sp: any): SubProject {
  return {
    id: String(sp.id),
    projectId: String(sp.parentprojectid ?? ""),
    name: sp.title ?? sp.name ?? "",
    leadContractorId: String(sp.leadcontractorslug ?? sp.leadcontractorid ?? ""),
    leadContractorOrgId: sp.leadcontractorid != null ? String(sp.leadcontractorid) : undefined,
    budgeted: Number(sp.budgeted ?? sp.targetamount ?? 0),
    spent: Number(sp.spent ?? 0),
    evidenced: Number(sp.evidenced ?? 0),
    status: (sp.status ?? "active") as SubProject["status"],
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapApiMilestone(m: any): Milestone {
  return {
    id: String(m.id),
    projectId: String(m.projectid),
    name: m.description ?? m.milestonenumber ?? String(m.id),
    targetDate: m.enddate ? new Date(m.enddate).toISOString().slice(0, 10) : "",
    status: (m.status ?? "planned") as Milestone["status"],
    budgetSharePct: 0,
    plannedBudget: m.plannedbudget != null ? Number(m.plannedbudget) : undefined,
    evidenceRequirements: Array.isArray(m.evidencerequirements) ? m.evidencerequirements : [],
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapApiSiteVisit(sv: any): SiteVisit {
  return {
    id: String(sv.id),
    projectId: String(sv.projectid ?? ""),
    milestoneId: sv.milestoneid != null ? String(sv.milestoneid) : undefined,
    visitorId: String(sv.visitorid ?? ""),
    startedAt: sv.startedat ?? undefined,
    completedAt: sv.completedat ?? undefined,
    status: (sv.status ?? "planned") as SiteVisit["status"],
    checklist: Array.isArray(sv.checklist) ? sv.checklist : [],
    captures: Array.isArray(sv.captures) ? sv.captures : [],
    summary: sv.summary ?? undefined,
    findingIds: Array.isArray(sv.findingids) ? sv.findingids : [],
    reportId: sv.reportid != null ? String(sv.reportid) : undefined,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapApiReport(r: any): Report {
  return {
    id: String(r.id),
    title: r.title ?? "",
    type: r.type as Report["type"],
    scope: r.scope as Report["scope"],
    programmeId: String(r.programmeid ?? ""),
    projectId: r.projectid != null ? String(r.projectid) : undefined,
    audience: r.audience ?? "",
    periodLabel: r.periodlabel ?? "",
    periodStart: r.periodstart ?? "",
    periodEnd: r.periodend ?? "",
    createdAt: r.createdat ?? "",
    updatedAt: r.updatedat ?? "",
    authorId: String(r.authorid ?? ""),
    sections: Array.isArray(r.sections) ? r.sections : [],
    visibleTo: Array.isArray(r.visibleto) ? r.visibleto : [],
    draftedByAI: Boolean(r.draftedbyai),
    limitedData: Boolean(r.limiteddata),
    notes: r.notes ?? undefined,
    reviewedById: r.reviewedbyid != null ? String(r.reviewedbyid) : undefined,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapApiAuthorityGrant(g: any): AuthorityGrant {
  return {
    id: String(g.id),
    granterId: String(g.granter_user_id ?? ""),
    granteeId: String(g.grantee_user_id ?? ""),
    granteeOrgId: String(g.grantee_org_id ?? ""),
    scope: typeof g.scope === "object" && g.scope !== null ? g.scope : {},
    envelope: Number(g.envelope ?? 0),
    selfAuthThreshold: Number(g.self_auth_threshold ?? 0),
    status: "active",
  };
}

/** The active profile object. When a real auth session exists (post-login)
 *  the profile is derived from the JWT user. Falls back to seed data in dev. */
export function useActiveProfile(): Profile {
  const authUser = useAuthStore((s) => s.user);
  const seedId = useStore((s) => s.activeProfileId);
  const devMode = useStore((s) => s.devMode);
  // Demo (Developer mode) ON: the profile switcher drives identity, so a single
  // logged-in session can walk every persona (raise as reporter, approve as
  // prime, release as funder). OFF: the real auth user is the acting identity.
  if (devMode) return profilesById[seedId] ?? devProfile;
  if (authUser) return buildProfileFromUser(authUser);
  return profilesById[seedId] ?? devProfile;
}

/** The seed person id the active profile acts as (for audit attribution). */
export function useActorId(): string {
  return useActiveProfile().personId;
}

/**
 * The seed updater bound to the active workspace.
 *
 * Off demo mode this also refreshes the audit trail. Every mutation hook in
 * useMutations.ts calls the backend and then mirrors the change into the local
 * seed through here, so this is the one choke point that knows "something just
 * changed server-side". Without it a freshly raised ask wouldn't appear in the
 * trail until the 30s staleTime lapsed, because the audit screens now read the
 * API rather than the local mirror. In demo mode the audit query is disabled,
 * so the invalidation is a no-op.
 */
export function useMutate() {
  const updateActiveSeed = useStore((s) => s.updateActiveSeed);
  const queryClient = useQueryClient();
  return useCallback(
    (updater: (seed: WorkspaceSeed) => WorkspaceSeed) => {
      updateActiveSeed(updater);
      if (!demoModeActive()) void queryClient.invalidateQueries({ queryKey: ["audit"] });
    },
    [updateActiveSeed, queryClient],
  );
}

/** The active workspace definition. */
export function useActiveWorkspace(): Workspace {
  const id = useStore((s) => s.activeWorkspaceId);
  return workspacesById[id];
}

/** A workspace definition by id (defaults to active). */
export function useWorkspace(workspaceId?: string): Workspace {
  const activeId = useStore((s) => s.activeWorkspaceId);
  return workspacesById[workspaceId ?? activeId];
}

/** The full working seed for a workspace (defaults to active). */
export function useSeed(workspaceId?: string): WorkspaceSeed {
  const activeId = useStore((s) => s.activeWorkspaceId);
  const id = workspaceId ?? activeId;
  return useStore((s) => s.seeds[id]);
}

/** The numeric DB programme ID for a workspace, or undefined if not mapped. */
export function useActiveProgrammeId(workspaceId?: string): string | undefined {
  const ws = useWorkspace(workspaceId);
  return ws?.programmeId != null ? String(ws.programmeId) : undefined;
}

/** Core programme detail (projects + subProjects) fetched from the API. Internal. */
function useApiProgrammeData(workspaceId?: string) {
  const programmeId = useActiveProgrammeId(workspaceId);
  return useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ["programme-data", programmeId],
    queryFn: () => fetchWithOfflineCache(`programme-data:${programmeId}`, () => api.getProgramme(programmeId!)),
    enabled: !demoModeActive() && !!programmeId,
    staleTime: 60_000,
    // Otherwise TanStack Query pauses this query (never calls queryFn at
    // all) while the browser reports offline and nothing is cached yet in
    // this tab's in-memory cache - the exact case fetchWithOfflineCache's
    // IndexedDB fallback exists for (e.g. the Raise form's project
    // dropdown after a fresh reload while offline).
    networkMode: "always",
  });
}

export function useProgramme(workspaceId?: string) {
  const { data: apiData } = useApiProgrammeData(workspaceId);
  const seed = useSeed(workspaceId);
  if (apiData) {
    return { ...seed.programme, id: String((apiData as any).id), name: (apiData as any).name };
  }
  return seed.programme;
}

export function useProjects(workspaceId?: string): Project[] {
  const programmeId = useActiveProgrammeId(workspaceId);
  const { data: apiData } = useApiProgrammeData(workspaceId);
  const seed = useSeed(workspaceId);
  const raw = (apiData as any)?.projects as unknown[] | undefined;
  // Memoised: an un-memoised `raw.map(...)` returns a fresh array identity on
  // every render, and callers list this list in `useEffect` deps. An effect that
  // fetches and then setStates would re-fire on its own re-render — an unbounded
  // request loop that hangs the page (it hammered /approval/.../approver-queue).
  return useMemo(
    () => (raw ? raw.map((p) => mapApiProject(p, programmeId!)) : seed.projects),
    [raw, programmeId, seed.projects],
  );
}

export function useProject(projectId: string, workspaceId?: string): Project | undefined {
  return useProjects(workspaceId).find((p) => p.id === projectId);
}

/**
 * True while the API programme detail is on its first load. Lets a page tell
 * "still fetching" apart from "genuinely not found": until the query resolves,
 * `useProjects` falls back to seed projects (slug ids), so an API project looked
 * up by its numeric DB id won't be found yet. In demo mode / when the workspace
 * has no programmeId the query is disabled, so this is false (seed is authoritative).
 */
export function useProgrammeLoading(workspaceId?: string): boolean {
  return useApiProgrammeData(workspaceId).isLoading;
}

export type CrossDealProject = Project & { workspaceId: string; contractorName: string };

/**
 * Every individual project across every known workspace/deal, for the
 * Oversight/Funder "Projects" nav item (a genuine cross-deal list, not the
 * per-workspace one `useProjects` gives after clicking into a single deal).
 * No persona argument — mirrors useDealCards()'s convention of "hook
 * returns everything, the screen applies its own visibility filtering"
 * (see FunderDashboardHome.tsx's isShared, which FunderProjectsPage mirrors
 * on top of this hook's result).
 *
 * Deliberately does NOT reuse useProjectSummaries(): that one sources ids
 * from the boot-time /api/workspace-seed snapshot, not this hook's live
 * mapApiProject mapping, so its ids aren't guaranteed to match what
 * /workspace/:workspaceId/projects/:projectId resolves - a row built from
 * it could dead-end on click. It also pulls in feasibility-only "extra
 * programmes" with no real workspace at all, which would be unclickable.
 */
export function useCrossDealProjects(): {
  projects: CrossDealProject[];
  isLoading: boolean;
  /** Non-empty means a partial result (Promise.allSettled), not a hard error. */
  failedWorkspaceIds: string[];
  refetch: () => void;
} {
  const demo = demoModeActive();
  const allSeeds = useAllSeeds();

  const query = useQuery({
    queryKey: ["cross-deal-projects"],
    queryFn: async () => {
      const settled = await Promise.allSettled(
        workspaces.map(async (w) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const programme = (await api.getProgramme(String(w.programmeId))) as any;
          // .projects, not .allProjects - top-level projects only, matching
          // what useProjects() itself reads. .allProjects also includes
          // sub-projects (resolveMyHomeWorkspaceId uses it for a different,
          // membership-matching purpose).
          return { workspaceId: w.id, raw: (programme?.projects ?? []) as unknown[] };
        }),
      );
      const fulfilled: { workspaceId: string; raw: unknown[] }[] = [];
      const failedWorkspaceIds: string[] = [];
      settled.forEach((r, i) => {
        if (r.status === "fulfilled") fulfilled.push(r.value);
        else failedWorkspaceIds.push(workspaces[i].id);
      });
      return { fulfilled, failedWorkspaceIds };
    },
    enabled: !demo,
    staleTime: 60_000,
  });

  // Per-workspace org index (not merged globally) - leadContractorId slugs
  // aren't guaranteed unique across workspaces.
  const orgIndexByWorkspace = useMemo(() => {
    const index: Record<string, Record<string, Organisation>> = {};
    for (const seed of allSeeds) {
      index[seed.workspace.id] = Object.fromEntries(seed.organisations.map((o) => [o.id, o]));
    }
    return index;
  }, [allSeeds]);

  const projects = useMemo<CrossDealProject[]>(() => {
    if (demo) {
      return workspaces.flatMap((w) => {
        const seed = allSeeds.find((s) => s.workspace.id === w.id);
        if (!seed) return [];
        return seed.projects.map((p) => ({
          ...p,
          workspaceId: w.id,
          contractorName: orgIndexByWorkspace[w.id]?.[p.leadContractorId]?.name ?? "—",
        }));
      });
    }
    const fulfilled = query.data?.fulfilled ?? [];
    return fulfilled.flatMap(({ workspaceId, raw }) => {
      const w = workspacesById[workspaceId];
      return raw.map((p) => {
        const mapped = mapApiProject(p, String(w.programmeId));
        return {
          ...mapped,
          workspaceId,
          contractorName: orgIndexByWorkspace[workspaceId]?.[mapped.leadContractorId]?.name ?? "—",
        };
      });
    });
  }, [demo, allSeeds, orgIndexByWorkspace, query.data]);

  return {
    projects,
    isLoading: !demo && query.isLoading,
    failedWorkspaceIds: demo ? [] : (query.data?.failedWorkspaceIds ?? []),
    refetch: () => void query.refetch(),
  };
}

/** True when the active profile is a contractor (prime or sub). */
export function useIsContractor(): boolean {
  return useActiveProfile().group === "contractor";
}

/**
 * Projects the active profile may see. Oversight, funder and platform see every
 * project; a contractor sees only projects their organisation leads or holds a
 * sub-project on (governance: they never see the whole programme fund).
 */
export function useVisibleProjects(workspaceId?: string): Project[] {
  const profile = useActiveProfile();
  const allProjects = useProjects(workspaceId);
  const allSubProjects = useAllSubProjects(workspaceId);
  // Memoised for the same reason as useProjects — a fresh array identity per
  // render turns any consumer's fetch-then-setState effect into a request loop.
  return useMemo(() => {
    if (profile.group !== "contractor") return allProjects;
    const orgId = profile.organisationId;
    const subProjectProjectIds = new Set(
      allSubProjects.filter((s) => (s.leadContractorOrgId ?? s.leadContractorId) === orgId).map((s) => s.projectId),
    );
    return allProjects.filter(
      (p) => (p.leadContractorOrgId ?? p.leadContractorId) === orgId || subProjectProjectIds.has(p.id),
    );
  }, [profile.group, profile.organisationId, allProjects, allSubProjects]);
}

// Cascade roles a profile may appoint, mirroring the backend zeus-cascade
// role_pack (prime_lead → prime_member/sub_lead, sub_lead → sub_member/reporter).
const DEMO_PRIME_APPOINTABLE = ["prime_member", "sub_lead"];
const DEMO_SUB_APPOINTABLE = ["sub_member", "reporter"];
const DEMO_FULL_APPOINTABLE = ["prime_lead", "sub_lead", "prime_member", "sub_member", "reporter"];

function demoAppointableRoles(profile: Profile): string[] {
  const perms = profile.permissions ?? [];
  if (perms.includes("contractor.lead")) return DEMO_PRIME_APPOINTABLE;
  if (perms.includes("subcontractor.lead")) return DEMO_SUB_APPOINTABLE;
  if (profile.group === "oversight" || profile.group === "other") return DEMO_FULL_APPOINTABLE;
  return [];
}

/**
 * Projects (+ sub-projects) the current user may invite people into, with the
 * cascade roles they can appoint on each (§9.5 invite scope). Dual-mode:
 *  - production: the backend endpoint keyed on the authed (JWT) user;
 *  - demo: derived from the seed, scoped to the active ProfileSwitcher profile,
 *    so the switcher drives it with no real login.
 */
export function useInvitableProjects(workspaceId?: string): InvitableProject[] {
  const profile = useActiveProfile();
  const allProjects = useProjects(workspaceId);
  const allSubProjects = useAllSubProjects(workspaceId);

  const { data: apiData } = useQuery({
    queryKey: ["invitable-projects"],
    queryFn: () => api.getInvitableProjects(),
    enabled: !demoModeActive(),
    staleTime: 60_000,
  });
  if (apiData) return apiData;

  // Demo path — seed scoped to the active profile.
  const roles = demoAppointableRoles(profile);
  if (roles.length === 0) return [];
  const orgId = profile.organisationId;
  const perms = profile.permissions ?? [];
  const inviteAnywhere = profile.group === "oversight" || profile.group === "other";
  const wholeProjectRole = inviteAnywhere
    ? "stratus_admin"
    : perms.includes("subcontractor.lead")
      ? "sub_lead"
      : "prime_lead";

  const subsByParent = new Map<string, SubProject[]>();
  for (const sp of allSubProjects) {
    const list = subsByParent.get(sp.projectId) ?? [];
    list.push(sp);
    subsByParent.set(sp.projectId, list);
  }

  const result: InvitableProject[] = [];
  for (const p of allProjects) {
    const childSubs = subsByParent.get(p.id) ?? [];
    const leadsProject = inviteAnywhere || (p.leadContractorOrgId ?? p.leadContractorId) === orgId;
    const ledSubs = leadsProject
      ? childSubs
      : childSubs.filter((sp) => (sp.leadContractorOrgId ?? sp.leadContractorId) === orgId);
    if (!leadsProject && ledSubs.length === 0) continue;

    result.push({
      projectId: p.id,
      name: p.name,
      slug: p.id,
      // When the user only leads a sub-project of this project, the parent is a
      // navigation shell with no whole-project invite (empty appointableRoles).
      myRole: leadsProject ? wholeProjectRole : "",
      appointableRoles: leadsProject ? roles : [],
      subProjects: ledSubs.map((sp) => ({
        projectId: sp.id,
        name: sp.name,
        slug: sp.id,
        myRole: perms.includes("subcontractor.lead") ? "sub_lead" : wholeProjectRole,
        appointableRoles: roles,
        // Seed sub-projects have no backend row; target the parent project's
        // slug so a demo invite still creates a real, openable link.
        inviteTargetId: p.id,
      })),
    });
  }
  return result;
}

/** Display shape for a project's People table — real projectmembership rows
 * carry a real accept/pending status; demo rows are synthesized as "active"
 * since the seed has nothing to demonstrate a pending state with. */
export type TeamMember = {
  id: string;
  /** The real projectmembership.id, for removeProjectMember - null in demo
   * mode, where there's no real membership row to reference. */
  membershipId: number | null;
  name: string;
  role: string;
  status: "pending" | "active" | "suspended" | "revoked";
  organisationName: string | null;
};

/** Real project members (with accept status) when not in demo mode; falls
 * back to the seed's people list, scoped to the project's own org(s), in
 * demo mode. TeamTab previously read only the seed path regardless of mode,
 * so an accepted invite never showed up anywhere. */
export function useProjectMembers(projectId: string, workspaceId?: string): { members: TeamMember[]; isLoading: boolean } {
  const demo = demoModeActive();
  const seed = useSeed(workspaceId);
  const { data: apiData, isLoading } = useQuery({
    queryKey: ["project-members", projectId],
    queryFn: () => api.getProjectMembers(projectId),
    enabled: !demo && !!projectId,
    staleTime: 30_000,
  });

  if (!demo) {
    const members: TeamMember[] = (apiData ?? []).map((m) => ({
      id: String(m.userId),
      membershipId: m.membershipId,
      name: m.name,
      role: m.role,
      status: m.status,
      organisationName: m.organisationName,
    }));
    return { members, isLoading };
  }

  const project = seed.projects.find((p) => p.id === projectId);
  const subs = seed.subProjects.filter((sp) => sp.projectId === projectId);
  const orgIds = new Set([project?.leadContractorId, ...subs.map((s) => s.leadContractorId)].filter(Boolean) as string[]);
  const members: TeamMember[] = seed.people
    .filter((p) => orgIds.has(p.organisationId))
    .map((p) => ({
      id: p.id,
      membershipId: null,
      name: p.fullName,
      role: p.role,
      status: "active",
      organisationName: seed.organisations.find((o) => o.id === p.organisationId)?.name ?? null,
    }));
  return { members, isLoading: false };
}

/** Display shape for the Team tab's Organisations table — unified across
 * real orgs (organisationType/kycState/status from the `organisation` table)
 * and demo orgs (a different, seed-only vocabulary), so the table can render
 * either without caring which mode produced it. */
export type ProjectOrgRow = { id: string; name: string; tier: "prime" | "sub"; kycState: string; status: string };

/** Real lead-contractor org for this project and each of its sub-projects,
 * when not in demo mode; falls back to the seed-derived set otherwise.
 * TeamTab previously read only the seed path regardless of mode, so a real
 * project's actual lead contractor never showed up here. */
export function useProjectOrganisations(projectId: string, workspaceId?: string): { organisations: ProjectOrgRow[]; isLoading: boolean } {
  const demo = demoModeActive();
  const seed = useSeed(workspaceId);
  const { data: apiData, isLoading } = useQuery({
    queryKey: ["project-organisations", projectId],
    queryFn: () => api.getProjectOrganisations(projectId),
    enabled: !demo && !!projectId,
    staleTime: 30_000,
  });

  if (!demo) {
    const rows: ProjectOrgRow[] = [];
    const seen = new Set<number>();
    const pushOrg = (org: ProjectOrganisationSummary | null | undefined) => {
      if (!org || seen.has(org.id)) return;
      seen.add(org.id);
      rows.push({
        id: String(org.id),
        name: org.name,
        tier: org.organisationType === "subcontractor" ? "sub" : "prime",
        kycState: org.kycState,
        status: org.status,
      });
    };
    pushOrg(apiData?.project?.leadContractor);
    apiData?.subProjects.forEach((sp) => pushOrg(sp.leadContractor));
    return { organisations: rows, isLoading };
  }

  const project = seed.projects.find((p) => p.id === projectId);
  const subs = seed.subProjects.filter((sp) => sp.projectId === projectId);
  const orgIds = new Set([project?.leadContractorId, ...subs.map((s) => s.leadContractorId)].filter(Boolean) as string[]);
  const organisations: ProjectOrgRow[] = seed.organisations
    .filter((o) => orgIds.has(o.id))
    .map((o) => ({ id: o.id, name: o.name, tier: o.tier === "prime" ? "prime" : "sub", kycState: o.kycState, status: o.status }));
  return { organisations, isLoading: false };
}

/** All sub-projects for the workspace — from programme API or seed fallback. */
export function useAllSubProjects(workspaceId?: string): SubProject[] {
  const { data: apiData } = useApiProgrammeData(workspaceId);
  const seed = useSeed(workspaceId);
  const raw = (apiData as any)?.subProjects as unknown[] | undefined;
  return useMemo(
    () => (raw ? raw.map(mapApiSubProject) : seed.subProjects),
    [raw, seed.subProjects],
  );
}

export function useSubProjects(projectId: string, workspaceId?: string): SubProject[] {
  const { data: apiData } = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ["sub-projects", projectId],
    queryFn: () => fetchWithOfflineCache(`sub-projects:${projectId}`, () => api.getSubProjects(projectId)),
    enabled: !demoModeActive() && !!projectId,
    staleTime: 60_000,
    networkMode: "always",
  });
  const seed = useSeed(workspaceId);
  return useMemo(
    () =>
      apiData
        ? (apiData as unknown[]).map(mapApiSubProject)
        : seed.subProjects.filter((sp) => sp.projectId === projectId),
    [apiData, seed.subProjects, projectId],
  );
}

/**
 * ZEUS-135 — a project's governed-action history (creation, direct edits,
 * status changes, edit-review cycle), including justification. No demo-seed
 * equivalent exists (this is real governance data written by the backend's
 * recordGovernedAction) — returns empty in demo mode rather than falling
 * back to seed data, since a seeded project was never created via this flow.
 */
export function useProjectGovernanceHistory(projectId: string) {
  const { data } = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ["project-governance-history", projectId],
    queryFn: () => api.getProjectGovernanceHistory(projectId),
    enabled: !demoModeActive() && !!projectId,
    staleTime: 30_000,
  });
  return data ?? [];
}

/**
 * The audit trail for a scope, from the real hash-chained ledger.
 *
 * Off demo mode this reads GET /api/audit; in demo mode it stays on the seed
 * blob, filtered exactly as the screens used to filter it inline, so the demo
 * walkthrough is unchanged (confirmed decision, 2026-08-25).
 *
 * Previously every audit surface read `seed.auditEvents` directly. That blob is
 * static — mutations appended to the in-memory copy for the session and a reload
 * reverted it — so real users' actions were recorded by the backend and never
 * shown. Visibility is enforced server-side; the scope passed here is a filter,
 * not a permission.
 */
export function useAuditEvents(
  scope?: { projectId?: string; subProjectId?: string },
  workspaceId?: string,
): AuditEvent[] {
  const programmeId = useActiveProgrammeId(workspaceId);
  const projectId = scope?.projectId;
  const subProjectId = scope?.subProjectId;
  const { data: apiData } = useQuery({
    queryKey: ["audit", programmeId, projectId, subProjectId],
    queryFn: () =>
      fetchWithOfflineCache(`audit:${programmeId}:${projectId ?? ""}:${subProjectId ?? ""}`, () =>
        api.getAuditTrail({ programmeId, projectId, subProjectId }),
      ),
    // A project/sub-project scope is self-contained; the workspace-level tabs
    // need the programme id before they can ask for anything meaningful.
    enabled: !demoModeActive() && (!!programmeId || !!projectId || !!subProjectId),
    staleTime: 30_000,
    networkMode: "always",
  });
  const seed = useSeed(workspaceId);
  return useMemo(() => {
    if (apiData) return apiData;
    return seed.auditEvents.filter((e) => {
      if (projectId && e.scope.projectId !== projectId) return false;
      if (subProjectId && e.scope.subProjectId !== subProjectId) return false;
      return true;
    });
  }, [apiData, seed.auditEvents, projectId, subProjectId]);
}

export function useIsSubContractor(workspaceId?: string): boolean {
  const profile = useActiveProfile();
  const allProjects = useProjects(workspaceId);
  const allSubProjects = useAllSubProjects(workspaceId);
  if (profile.group !== "contractor") return false;
  const orgId = profile.organisationId;
  const leadsProject = allProjects.some((p) => (p.leadContractorOrgId ?? p.leadContractorId) === orgId);
  const leadsSub = allSubProjects.some((s) => (s.leadContractorOrgId ?? s.leadContractorId) === orgId);
  return !leadsProject && leadsSub;
}

export function useVisibleSubProjects(workspaceId?: string): SubProject[] {
  const profile = useActiveProfile();
  const allSubProjects = useAllSubProjects(workspaceId);
  return useMemo(
    () =>
      profile.group !== "contractor"
        ? allSubProjects
        : allSubProjects.filter((s) => (s.leadContractorOrgId ?? s.leadContractorId) === profile.organisationId),
    [profile.group, profile.organisationId, allSubProjects],
  );
}

export function useSubProject(subProjectId: string, workspaceId?: string): SubProject | undefined {
  return useAllSubProjects(workspaceId).find((sp) => sp.id === subProjectId);
}

export function useMilestones(projectId: string, workspaceId?: string): Milestone[] {
  const { data: apiData } = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ["milestones", projectId],
    queryFn: () => fetchWithOfflineCache(`milestones:${projectId}`, () => api.getMilestones(projectId)),
    enabled: !demoModeActive() && !!projectId,
    staleTime: 60_000,
    networkMode: "always",
  });
  const seed = useSeed(workspaceId);
  if (apiData) return (apiData as unknown[]).map(mapApiMilestone);
  // Project-level milestones only: sub-project milestones live under their
  // sub-project (useSubProjectMilestones) and would distort the project
  // delivery timeline and tranche maths if mixed in here.
  return seed.milestones.filter((m) => m.projectId === projectId && !m.subProjectId);
}

/** Milestones owned by one sub-project. Seed-only for now — the backend has no
 *  sub-project milestone rows yet, so there is no API branch to consult. */
export function useSubProjectMilestones(subProjectId: string, workspaceId?: string): Milestone[] {
  const seed = useSeed(workspaceId);
  if (!subProjectId) return [];
  return seed.milestones.filter((m) => m.subProjectId === subProjectId);
}

/** Lookup helpers that resolve people and organisations by id. */
export function usePeopleIndex(workspaceId?: string): Record<string, Person> {
  const people = useSeed(workspaceId).people;
  return Object.fromEntries(people.map((p) => [p.id, p]));
}

export function useOrgIndex(workspaceId?: string): Record<string, Organisation> {
  const orgs = useSeed(workspaceId).organisations;
  return Object.fromEntries(orgs.map((o) => [o.id, o]));
}

/** Spend requests filtered by scope (defaults to whole workspace). */
export function useSpendRequests(
  scope?: { projectId?: string; subProjectId?: string; state?: SpendRequest["state"] },
  workspaceId?: string,
): SpendRequest[] {
  const reqs = useSeed(workspaceId).spendRequests;
  return reqs.filter((r) => {
    if (scope?.projectId && r.projectId !== scope.projectId) return false;
    if (scope?.subProjectId && r.subProjectId !== scope.subProjectId) return false;
    if (scope?.state && r.state !== scope.state) return false;
    return true;
  });
}

/** All workspace seeds (the working copies in the store). */
export function useAllSeeds(): WorkspaceSeed[] {
  const seeds = useStore((s) => s.seeds);
  return Object.values(seeds);
}

/** Cross-programme summary rows for the Stratus oversight tables. */
export function useProgrammeSummaries(): ProgrammeSummary[] {
  const seeds = useAllSeeds();
  const fromSeeds: ProgrammeSummary[] = seeds.map((seed) => {
    const ms = seed.milestones;
    const disbursed = ms.filter((m) => m.status === "disbursed" || m.status === "closed").length;
    return {
      id: seed.programme.id,
      workspaceId: seed.workspace.id,
      name: seed.programme.name,
      funder: seed.workspace.funder.name,
      recipient: seed.workspace.recipient.name,
      totalBudget: seed.programme.totalBudget,
      milestonesDisbursed: `${disbursed} / ${ms.length}`,
      milestonesPct: ms.length ? disbursed / ms.length : 0,
      alertState: seed.programme.alertState,
      status: seed.programme.status,
      projects: seed.projects.length,
      phase: seed.programme.phase,
      funderVisibility: seed.programme.funderVisibility,
    };
  });
  return [...fromSeeds, ...extraProgrammes];
}

/** Cross-programme flat project list for the Stratus projects tab. */
export function useProjectSummaries(): ProjectSummary[] {
  const seeds = useAllSeeds();
  const fromSeeds: ProjectSummary[] = seeds.flatMap((seed) => {
    const orgIndex = Object.fromEntries(seed.organisations.map((o) => [o.id, o]));
    return seed.projects.map((p) => {
      const consumed = p.budgeted ? p.spent / p.budgeted : 0;
      const risk: "GREEN" | "AMBER" | "RED" = consumed > 0.7 ? "AMBER" : "GREEN";
      return {
        id: p.id,
        workspaceId: seed.workspace.id,
        name: p.name,
        programme: seed.programme.name,
        contractor: orgIndex[p.leadContractorId]?.name ?? "—",
        budgeted: p.budgeted,
        spent: p.spent,
        status: p.status,
        risk,
      };
    });
  });
  return [...fromSeeds, ...karamojaProjects];
}

// ---------------------------------------------------------------------------
// Multi-currency display (spec §2.6)
// ---------------------------------------------------------------------------

/** The active preferred display currency. */
export function useDisplayCurrency(): Currency {
  return useStore((s) => s.displayCurrency);
}

export function useSetDisplayCurrency() {
  return useStore((s) => s.setDisplayCurrency);
}

/**
 * A formatter bound to the active display currency. Pass a UGX-base figure and an
 * optional source-currency override (e.g. the funder's EUR commitments).
 */
export function useMoney() {
  const display = useDisplayCurrency();
  return (baseUgx: number, source?: CurrencyCode): string =>
    formatMoneyIn(baseUgx, (source ?? display) as CurrencyCode);
}

/**
 * Like useMoney but returns the compact value and the currency code separately, for
 * Stat tiles that render a big value with a small unit suffix.
 */
export function useMoneyParts() {
  const display = useDisplayCurrency();
  return (baseUgx: number, source?: CurrencyCode): { value: string; unit: string } => {
    const code = (source ?? display) as CurrencyCode;
    return { value: formatCompactCurrency(convert(baseUgx, "UGX", code)), unit: code };
  };
}

// ---------------------------------------------------------------------------
// Reports + evidence (spec §5.1, §2.9)
// ---------------------------------------------------------------------------

/** Reports visible to the active profile's role. */
export function useReports(workspaceId?: string): Report[] {
  // il_report.programmeid holds the programme slug (e.g. "prog-fire-stations") —
  // the same value useMutations sends on createReport and the ZEUS seed inserts.
  const seed = useSeed(workspaceId); // always called before any conditional return — rules of hooks
  const programmeSlug = seed?.programme.id;
  const { data: apiData } = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ["reports", programmeSlug],
    queryFn: () => api.getReports(programmeSlug!),
    enabled: !demoModeActive() && !!programmeSlug,
    staleTime: 30_000,
  });
  const profile = useActiveProfile();
  const role = profile.group === "funder" ? "funder" : profile.group === "contractor" ? "contractor" : "oversight";
  const seedReports = seed?.reports ?? [];
  if (apiData) {
    const mapped = (apiData as unknown[]).map(mapApiReport);
    return mapped.filter((r) => r.visibleTo.includes(role));
  }
  return seedReports.filter((r) => r.visibleTo.includes(role));
}

export function useReport(reportId: string, workspaceId?: string): Report | undefined {
  return useReports(workspaceId).find((r) => r.id === reportId);
}

/** Evidence items, optionally scoped to a single ask. */
export function useEvidence(scope?: { askId?: string }, workspaceId?: string): EvidenceItem[] {
  const items = useSeed(workspaceId).evidence ?? [];
  return scope?.askId ? items.filter((e) => e.linkedAskId === scope.askId) : items;
}

// ---------------------------------------------------------------------------
// Cascading delegated authority (v3 §Chunk 1)
// ---------------------------------------------------------------------------

export function useAuthorityGrants(workspaceId?: string): AuthorityGrant[] {
  const programmeId = useActiveProgrammeId(workspaceId);
  const seed = useSeed(workspaceId); // always called before any conditional return — rules of hooks
  const { data: apiData } = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ["authority-grants", programmeId],
    queryFn: () => api.getAuthorityGrants(programmeId!),
    enabled: !demoModeActive() && !!programmeId,
    staleTime: 60_000,
  });
  const seedGrants = seed?.authorityGrants ?? [];
  if (apiData) return ((apiData as any).grants as unknown[] ?? []).map(mapApiAuthorityGrant);
  return seedGrants;
}

/** The grants a person holds (received) and the grants they have delegated downward. */
export function useMyAuthority(workspaceId?: string): { held: AuthorityGrant[]; delegated: AuthorityGrant[] } {
  const actorId = useActorId();
  const grants = useAuthorityGrants(workspaceId);
  return {
    held: grants.filter((g) => g.granteeId === actorId),
    delegated: grants.filter((g) => g.granterId === actorId),
  };
}

/** A person's self-authorisation threshold for a scope (max over matching active grants); 0 if none. */
export function resolveSelfAuthThreshold(
  grants: AuthorityGrant[],
  granteeId: string,
  scope: { projectId?: string; subProjectId?: string },
): number {
  const matches = grants.filter(
    (g) =>
      g.status === "active" &&
      g.granteeId === granteeId &&
      ((scope.subProjectId && g.scope.subProjectId === scope.subProjectId) ||
        (!g.scope.subProjectId && scope.projectId && g.scope.projectId === scope.projectId && !g.scope.subProjectId)),
  );
  return matches.reduce((m, g) => Math.max(m, g.selfAuthThreshold), 0);
}

/** At or below the threshold, spend is recorded directly; above, it needs pre-authorisation. */
export function capturePath(amount: number, threshold: number): "recorded" | "pre_auth" {
  return threshold > 0 && amount <= threshold ? "recorded" : "pre_auth";
}

// ---------------------------------------------------------------------------
// Materiality (v3 §Chunk 5)
// ---------------------------------------------------------------------------

export function useMateriality(): MaterialityConfig {
  return useStore((s) => s.materiality);
}
export function useSetMateriality() {
  return useStore((s) => s.setMateriality);
}

// ---------------------------------------------------------------------------
// Connections (v3 §Chunk 3)
// ---------------------------------------------------------------------------

export function useConnections(orgId?: string, workspaceId?: string): Connection[] {
  const all = useSeed(workspaceId).connections ?? [];
  return orgId ? all.filter((c) => c.organisationId === orgId) : all;
}

export function useFeedTransactions(
  scope?: { orgId?: string; unreconciledOnly?: boolean },
  workspaceId?: string,
): FeedTransaction[] {
  const all = useSeed(workspaceId).feedTransactions ?? [];
  return all.filter(
    (t) =>
      (!scope?.orgId || t.organisationId === scope.orgId) &&
      (!scope?.unreconciledOnly || t.status === "unreconciled"),
  );
}

// ---------------------------------------------------------------------------
// Assurance (v3 §Chunk 2) — how trustworthy each figure is, derived from its facts.
// ---------------------------------------------------------------------------

export const ASSURANCE_ORDER: AssuranceLevel[] = ["self_reported", "document_backed", "statement_matched", "verified"];

/** Derive a spend ask's assurance level from verification, statement match and evidence. */
export function assuranceOf(spend: SpendRequest, evidence: EvidenceItem[], spendItems: SpendItem[]): AssuranceLevel {
  if (spend.verifiedById) return "verified";
  if (spend.statementMatchRef) return "statement_matched";
  // Any attached proof lifts the figure off the bottom rung — a geotagged site
  // photo included. This previously counted only "receipt"/"document" evidence,
  // so a Reporter who submitted the photo the flow asks them for was still told
  // the spend was "only self-reported", with nothing on screen explaining that a
  // different file type was wanted. Spec §6.4 lists "photo of the goods"
  // alongside invoices and receipts without ranking them; the rungs above
  // (statement-matched, verified) are what separate corroborated figures.
  const hasDoc =
    (spend.documents?.length ?? 0) > 0 ||
    evidence.some((e) => e.linkedAskId === spend.id) ||
    spendItems.some((i) => i.spendRequestId === spend.id && i.evidence.length > 0);
  return hasDoc ? "document_backed" : "self_reported";
}

/** A gap = money is considered out but the figure is only self-reported (needs evidence, match or explanation). */
export function spendHasGap(spend: SpendRequest, level: AssuranceLevel): boolean {
  const moneyOut = spend.state === "committed" || spend.state === "evidence_submitted" || spend.state === "confirmed";
  return moneyOut && level === "self_reported";
}

/** A function bound to the active seed that returns the assurance level for any spend ask. */
export function useAssurance(workspaceId?: string) {
  const seed = useSeed(workspaceId);
  return (spend: SpendRequest): AssuranceLevel => assuranceOf(spend, seed.evidence ?? [], seed.spendItems);
}

// ---------------------------------------------------------------------------
// Heimdall — the AI analyst layer (Heimdall brief)
// ---------------------------------------------------------------------------

/** The analyst's name for this workspace (defaults to "Heimdall"). */
export function useAnalystName(workspaceId?: string): string {
  const ws = useWorkspace(workspaceId);
  return ws.terminology.analyst ?? ANALYST_NAME;
}

/** All Heimdall findings, optionally scoped to a single ask. */
export function useAiFindings(scope?: { askId?: string }, workspaceId?: string): AIFinding[] {
  const all = useSeed(workspaceId).aiFindings ?? [];
  return scope?.askId ? all.filter((f) => f.subjectAskId === scope.askId) : all;
}

/** Findings attached to one ask (a thin convenience wrapper). */
export function useFindingsForAsk(askId: string, workspaceId?: string): AIFinding[] {
  const all = useSeed(workspaceId).aiFindings ?? [];
  return findingsForAsk(all, askId);
}

/** A function bound to the active seed that returns Heimdall's review badge for any ask. */
export function useAiReviewFor(workspaceId?: string) {
  const seed = useSeed(workspaceId);
  return (ask: SpendRequest): AIReview => reviewForAsk(ask, seed.aiFindings ?? []);
}

/** Site visits, optionally scoped to a project. */
export function useSiteVisits(scope?: { projectId?: string }, workspaceId?: string): SiteVisit[] {
  // site_visit.programmeid holds the programme slug (e.g. "prog-fire-stations") —
  // the same value useMutations sends on startSiteVisit and the ZEUS seed inserts.
  const seed = useSeed(workspaceId); // always called before any conditional return — rules of hooks
  const programmeSlug = seed?.programme.id;
  const { data: apiData } = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ["site-visits", programmeSlug],
    queryFn: () => api.getSiteVisits(programmeSlug!),
    enabled: !demoModeActive() && !!programmeSlug,
    staleTime: 30_000,
  });
  const seedVisits = seed?.siteVisits ?? [];
  const all: SiteVisit[] = apiData
    ? (apiData as unknown[]).map(mapApiSiteVisit)
    : seedVisits;
  return scope?.projectId ? all.filter((v) => v.projectId === scope.projectId) : all;
}

/**
 * The effective site-visit question template: the compulsory core (always present,
 * from code) followed by oversight's custom questions. Custom questions come from
 * the backend (`site_visit_question`) in real mode, or the seed blob in demo mode.
 * The source is filtered to non-compulsory so a stale blob carrying core rows can't
 * duplicate them.
 */
export function useSiteVisitQuestions(workspaceId?: string): SiteVisitQuestion[] {
  const seed = useSeed(workspaceId);
  const programmeSlug = seed?.programme.id;
  const { data } = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ["site-visit-questions", programmeSlug],
    queryFn: () => api.getSiteVisitQuestions(programmeSlug!),
    enabled: !demoModeActive() && !!programmeSlug,
    staleTime: 30_000,
  });
  const source = !demoModeActive() ? (data ?? seed?.siteVisitQuestions ?? []) : (seed?.siteVisitQuestions ?? []);
  const custom = source.filter((q) => !q.compulsory);
  return [...COMPULSORY_SITE_VISIT_QUESTIONS, ...custom];
}

/** Inbound-funds declarations for the active workspace (pilot-readiness addendum §5). */
export function useDeclarations(workspaceId?: string) {
  return useSeed(workspaceId).declarations ?? [];
}

/** A function bound to the active seed returning the joint AI + human verification summary for a milestone. */
export function useVerificationSummary(workspaceId?: string) {
  const seed = useSeed(workspaceId);
  return (milestoneId: string): VerificationSummary => verificationSummary(seed, milestoneId);
}

/** Resolve the workspace terminology with sensible defaults. */
export function useTerminology(workspaceId?: string) {
  const ws = useWorkspace(workspaceId);
  return {
    primeContractor: ws.terminology.primeContractor ?? "Prime contractor",
    subcontractor: ws.terminology.subcontractor ?? "Subcontractor",
    reporter: ws.terminology.reporter ?? "Reporter",
    programme: ws.terminology.programme ?? "Programme",
    project: ws.terminology.project ?? "Project",
    subProject: ws.terminology.subProject ?? "Sub-project",
    spendAsk: ws.terminology.spendAsk ?? "Spend ask",
  };
}

/**
 * The dynamic, priority-driven "actions needed" rail - one hook, reused by
 * every persona's dashboard layout (StratusDashboardLayout, FunderDashboard
 * Layout, ProgrammeDashboard's contractor branch). Demo mode derives from
 * the seed (deriveActionPanelItems); real mode calls GET /api/action-panel
 * (server/helpers/actionPanel.js). Both paths run through the same
 * rankActionItems afterward, so severity-escalation and sort order are
 * identical regardless of source - see domain/actionPanel.ts.
 */
export function useActionPanelItems(workspaceId?: string): { title: string; items: ActionItem[]; isLoading: boolean } {
  const demo = demoModeActive();
  const seed = useSeed(workspaceId);
  const profile = useActiveProfile();
  const actorId = useActorId();
  const title = ACTION_PANEL_TITLE[profile.group] ?? "Needs You";

  const { data: apiData, isLoading } = useQuery({
    queryKey: ["action-panel"],
    queryFn: () => api.getActionPanelItems(),
    enabled: !demo,
    staleTime: 30_000,
  });

  const raw = demo ? deriveActionPanelItems(seed, profile, actorId) : apiData ?? [];
  return { title, items: rankActionItems(raw), isLoading: !demo && isLoading };
}
